import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { 
  Menu, Package, TrendingUp, AlertTriangle, Clock, CheckCircle,
  Eye, Trash2, Search, Calendar, RotateCcw, Plus, X, ChevronDown, ChevronUp, Edit,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const ApprovalReports = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [approvals, setApprovals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Modals
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [extendDays, setExtendDays] = useState(3);
  const [extendReason, setExtendReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [approvalsRes, customersRes] = await Promise.all([
        axiosInstance.get('/approvals'),
        axiosInstance.get('/customers')
      ]);
      setApprovals(approvalsRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const getCustomerMobile = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.mobile || '';
  };

  const getPurityMultiplier = (purity) => {
    const multipliers = { '24K': 1.0, '22K': 0.916, '18K': 0.75, '14K': 0.585 };
    return multipliers[purity] || 0.916;
  };

  const calculateGrossWeight = (approval) => {
    if (!approval.items || approval.items.length === 0) return 0;
    // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
    return approval.items.reduce((sum, item) => {
      let weight = 0;
      if (item.gross_wt) {
        weight = parseFloat(item.gross_wt);
      } else if (item.weight_type === 'Fixed') {
        weight = parseFloat(item.weight_fixed) || 0;
      } else {
        weight = ((parseFloat(item.weight_min) || 0) + (parseFloat(item.weight_max) || 0)) / 2;
      }
      return sum + weight;
    }, 0);
  };

  const calculateNetWeight = (approval) => {
    if (!approval.items || approval.items.length === 0) return 0;
    return approval.items.reduce((sum, item) => {
      let weight = 0;
      if (item.net_wt) {
        weight = parseFloat(item.net_wt);
      } else if (item.gross_wt) {
        weight = parseFloat(item.gross_wt);
      } else if (item.weight_type === 'Fixed') {
        weight = parseFloat(item.weight_fixed) || 0;
      } else {
        weight = ((parseFloat(item.weight_min) || 0) + (parseFloat(item.weight_max) || 0)) / 2;
      }
      return sum + weight;
    }, 0);
  };

  const calculateFineWeight = (approval) => {
    if (!approval.items || approval.items.length === 0) return 0;
    return approval.items.reduce((sum, item) => {
      let netWt = 0;
      if (item.net_wt) {
        netWt = parseFloat(item.net_wt);
      } else if (item.gross_wt) {
        netWt = parseFloat(item.gross_wt);
      } else if (item.weight_type === 'Fixed') {
        netWt = parseFloat(item.weight_fixed) || 0;
      } else {
        netWt = ((parseFloat(item.weight_min) || 0) + (parseFloat(item.weight_max) || 0)) / 2;
      }
      return sum + (netWt * getPurityMultiplier(item.purity));
    }, 0);
  };

  const getApprovalStatus = (approval) => {
    if (approval.status === 'Returned') return { status: 'Returned', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    if (approval.status === 'Cancelled') return { status: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: X };
    
    if (!approval.return_date) {
      return { status: 'Active', color: 'bg-blue-100 text-blue-800', icon: Clock };
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const returnDate = new Date(approval.return_date);
    returnDate.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((returnDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle, days: Math.abs(diffDays) };
    } else if (diffDays === 0) {
      return { status: 'Due Today', color: 'bg-yellow-100 text-yellow-800', icon: Clock, days: 0 };
    } else {
      return { status: 'Active', color: 'bg-blue-100 text-blue-800', icon: Clock, days: diffDays };
    }
  };

  const getDaysDisplay = (approval) => {
    const statusInfo = getApprovalStatus(approval);
    if (statusInfo.status === 'Returned' || statusInfo.status === 'Cancelled') return '-';
    if (statusInfo.status === 'Overdue') return `${statusInfo.days}d overdue`;
    if (statusInfo.status === 'Due Today') return 'Due today';
    if (statusInfo.days !== undefined) return `${statusInfo.days}d left`;
    return '-';
  };

  // Filtered approvals
  const filteredApprovals = useMemo(() => {
    let filtered = [...approvals];
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => {
        const statusInfo = getApprovalStatus(a);
        if (statusFilter === 'active') return statusInfo.status === 'Active';
        if (statusFilter === 'overdue') return statusInfo.status === 'Overdue';
        if (statusFilter === 'due-today') return statusInfo.status === 'Due Today';
        if (statusFilter === 'returned') return statusInfo.status === 'Returned';
        return true;
      });
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      if (dateRange === 'today') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      } else if (dateRange === 'this_week') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
      } else if (dateRange === 'this_month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        endDate = new Date();
      } else if (dateRange === 'custom' && customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
      }
      
      if (startDate && endDate) {
        filtered = filtered.filter(a => {
          const approvalDate = new Date(a.approval_date || a.created_at);
          return approvalDate >= startDate && approvalDate <= endDate;
        });
      }
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.approval_id?.toLowerCase().includes(query) ||
        getCustomerName(a.customer_id)?.toLowerCase().includes(query) ||
        getCustomerMobile(a.customer_id)?.includes(query)
      );
    }
    
    // Sorting logic
    if (sortColumn) {
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortColumn) {
          case 'date':
            valueA = new Date(a.approval_date || a.created_at || 0).getTime();
            valueB = new Date(b.approval_date || b.created_at || 0).getTime();
            break;
          case 'id':
            valueA = (a.approval_id || '').toLowerCase();
            valueB = (b.approval_id || '').toLowerCase();
            break;
          case 'customer':
            valueA = (getCustomerName(a.customer_id) || '').toLowerCase();
            valueB = (getCustomerName(b.customer_id) || '').toLowerCase();
            break;
          case 'weight':
            valueA = parseFloat(a.total_gross_weight || 0);
            valueB = parseFloat(b.total_gross_weight || 0);
            break;
          case 'value':
            valueA = parseFloat(a.total_amount || 0);
            valueB = parseFloat(b.total_amount || 0);
            break;
          case 'status':
            valueA = getApprovalStatus(a).status;
            valueB = getApprovalStatus(b).status;
            break;
          default:
            return 0;
        }
        
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort: Overdue first, then by date (newest first)
      filtered.sort((a, b) => {
        const statusA = getApprovalStatus(a);
        const statusB = getApprovalStatus(b);
        
        // Overdue items first
        if (statusA.status === 'Overdue' && statusB.status !== 'Overdue') return -1;
        if (statusA.status !== 'Overdue' && statusB.status === 'Overdue') return 1;
        
        // Then by date
        const dateA = new Date(a.created_at || a.approval_date || 0);
        const dateB = new Date(b.created_at || b.approval_date || 0);
        return dateB - dateA;
      });
    }
    
    return filtered;
  }, [approvals, statusFilter, dateRange, customStartDate, customEndDate, searchQuery, customers, sortColumn, sortDirection]);


  // Sorting handlers
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-3 h-3 inline ml-1" /> : 
      <ArrowDown className="w-3 h-3 inline ml-1" />;
  };


  // Stats calculation
  const stats = useMemo(() => {
    const activeApprovals = approvals.filter(a => {
      const status = getApprovalStatus(a);
      return status.status !== 'Returned' && status.status !== 'Cancelled';
    });
    
    const overdueApprovals = approvals.filter(a => getApprovalStatus(a).status === 'Overdue');
    
    const totalGrossWeight = activeApprovals.reduce((sum, a) => sum + calculateGrossWeight(a), 0);
    const totalFineWeight = activeApprovals.reduce((sum, a) => sum + calculateFineWeight(a), 0);
    const totalItems = activeApprovals.reduce((sum, a) => sum + (a.items?.length || 0), 0);
    
    return {
      totalApprovals: activeApprovals.length,
      overdueCount: overdueApprovals.length,
      totalGrossWeight,
      totalFineWeight,
      totalItems
    };
  }, [approvals, customers]);

  // Actions
  const handleMarkReturned = async () => {
    if (!selectedApproval) return;
    
    try {
      await axiosInstance.put(`/approvals/${selectedApproval.approval_id}`, {
        status: 'Returned',
        returned_at: new Date().toISOString(),
        return_notes: returnNotes
      });
      toast.success('Approval marked as returned');
      setShowReturnModal(false);
      setSelectedApproval(null);
      setReturnNotes('');
      loadData();
    } catch (err) {
      console.error('Error marking returned:', err);
      toast.error('Failed to mark as returned');
    }
  };

  const handleExtendReturn = async () => {
    if (!selectedApproval || !extendDays) return;
    
    try {
      const currentReturnDate = selectedApproval.return_date ? new Date(selectedApproval.return_date) : new Date();
      const newReturnDate = new Date(currentReturnDate);
      newReturnDate.setDate(newReturnDate.getDate() + parseInt(extendDays));
      
      await axiosInstance.put(`/approvals/${selectedApproval.approval_id}`, {
        return_date: newReturnDate.toISOString().split('T')[0],
        extension_reason: extendReason,
        extended_at: new Date().toISOString()
      });
      toast.success(`Return date extended by ${extendDays} days`);
      setShowExtendModal(false);
      setSelectedApproval(null);
      setExtendDays(3);
      setExtendReason('');
      loadData();
    } catch (err) {
      console.error('Error extending return:', err);
      toast.error('Failed to extend return date');
    }
  };

  const handleDelete = async (approvalId) => {
    if (!window.confirm('Are you sure you want to delete this approval?')) return;
    
    try {
      await axiosInstance.delete(`/approvals/${approvalId}`);
      toast.success('Approval deleted');
      loadData();
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Failed to delete approval');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
      
      <div className="page-container">
        {/* Header */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors touch-target"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <div>
              <h1 className="page-title">Approval Reports</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Track and manage all approvals</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/approvals/create')}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Approval
          </button>
        </div>

        {/* Risk Summary Cards - Standardized Amber/Orange Theme */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md border-2 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">Active Approvals</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{stats.totalApprovals}</p>
              </div>
              <Package className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500 opacity-40" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md border-2 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">Overdue</p>
                <p className="text-xl sm:text-2xl font-bold text-red-700 mt-1">{stats.overdueCount}</p>
              </div>
              <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-red-500 opacity-40" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md border-2 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">Total Items Out</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{stats.totalItems}</p>
              </div>
              <Package className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500 opacity-40" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md border-2 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">Gross Wt at Risk</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{stats.totalGrossWeight.toFixed(2)}g</p>
              </div>
              <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500 opacity-40" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md border-2 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">Fine Wt at Risk</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{stats.totalFineWeight.toFixed(2)}g</p>
              </div>
              <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500 opacity-40" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-md">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, Customer, or Phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="overdue">Overdue</option>
              <option value="due-today">Due Today</option>
              <option value="returned">Returned</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </>
            )}
          </div>
        </div>

        {/* Approvals List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading approvals...</p>
          </div>
        ) : filteredApprovals.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No approvals found</p>
            <button
              onClick={() => navigate('/approvals/create')}
              className="mt-4 px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              Create New Approval
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-auto max-h-[calc(100vh-350px)] border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-amber-100 to-orange-100 sticky top-0 z-10">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-amber-200 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('id')}
                    >
                      Approval ID {getSortIcon('id')}
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-amber-200 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {getSortIcon('customer')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Items</th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-amber-200 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('weight')}
                    >
                      Gross Wt {getSortIcon('weight')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Net Wt</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Fine Wt</th>
                    <th 
                      className="px-4 py-3 text-center text-xs font-semibold text-gray-700 cursor-pointer hover:bg-amber-200 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('date')}
                    >
                      Given On {getSortIcon('date')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Return By</th>
                    <th 
                      className="px-4 py-3 text-center text-xs font-semibold text-gray-700 cursor-pointer hover:bg-amber-200 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('status')}
                    >
                      Status {getSortIcon('status')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Days</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApprovals.map((approval) => {
                    const statusInfo = getApprovalStatus(approval);
                    const StatusIcon = statusInfo.icon;
                    const isOverdue = statusInfo.status === 'Overdue';
                    
                    return (
                      <tr 
                        key={approval.approval_id} 
                        className={`hover:bg-amber-50 transition-colors ${isOverdue ? 'bg-red-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-semibold text-amber-700">{approval.approval_id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800">{getCustomerName(approval.customer_id)}</p>
                            <p className="text-xs text-gray-500">{getCustomerMobile(approval.customer_id)}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-semibold">
                            {approval.items?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{calculateGrossWeight(approval).toFixed(2)}g</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-700">{calculateNetWeight(approval).toFixed(2)}g</td>
                        <td className="px-4 py-3 text-right font-medium text-purple-700">{calculateFineWeight(approval).toFixed(2)}g</td>
                        <td className="px-4 py-3 text-center text-sm">{formatDate(approval.approval_date || approval.created_at)}</td>
                        <td className="px-4 py-3 text-center text-sm">{formatDate(approval.return_date)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                            {getDaysDisplay(approval)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/approvals/${approval.approval_id}`)}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/approvals/edit/${approval.approval_id}`)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {statusInfo.status !== 'Returned' && statusInfo.status !== 'Cancelled' && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedApproval(approval);
                                    setShowReturnModal(true);
                                  }}
                                  className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                  title="Mark Returned"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedApproval(approval);
                                    setShowExtendModal(true);
                                  }}
                                  className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                                  title="Extend Return"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {user.role === 'Admin' && (
                              <button
                                onClick={() => handleDelete(approval.approval_id)}
                                className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3 p-3">
              {filteredApprovals.map((approval) => {
                const statusInfo = getApprovalStatus(approval);
                const StatusIcon = statusInfo.icon;
                const isOverdue = statusInfo.status === 'Overdue';
                
                return (
                  <div 
                    key={approval.approval_id} 
                    className={`bg-white border-2 rounded-xl p-3 shadow-sm ${
                      isOverdue ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-bold text-amber-700 text-sm">{approval.approval_id}</p>
                        <p className="text-sm font-medium text-gray-800 mt-0.5">{getCustomerName(approval.customer_id)}</p>
                        <p className="text-xs text-gray-500">{getCustomerMobile(approval.customer_id)}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color} flex-shrink-0`}>
                        <StatusIcon className="w-3 h-3" />
                        <span className="hidden sm:inline">{statusInfo.status}</span>
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2 text-xs mb-3 bg-amber-50 rounded-lg p-2 border border-amber-200">
                      <div className="text-center">
                        <p className="text-gray-600 mb-0.5">Items</p>
                        <p className="font-bold text-amber-700">{approval.items?.length || 0}</p>
                      </div>
                      <div className="text-center border-x border-amber-200">
                        <p className="text-gray-600 mb-0.5">Gross</p>
                        <p className="font-bold text-gray-700">{calculateGrossWeight(approval).toFixed(2)}g</p>
                      </div>
                      <div className="text-center border-r border-amber-200">
                        <p className="text-gray-600 mb-0.5">Net</p>
                        <p className="font-bold text-amber-700">{calculateNetWeight(approval).toFixed(2)}g</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600 mb-0.5">Fine</p>
                        <p className="font-bold text-purple-700">{calculateFineWeight(approval).toFixed(2)}g</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3 pb-3 border-b border-gray-200">
                      <div>
                        <p className="text-gray-500 mb-0.5">Given Date</p>
                        <p className="font-semibold text-gray-700">{formatDate(approval.approval_date || approval.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-0.5">Return Date</p>
                        <p className={`font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>{formatDate(approval.return_date)}</p>
                      </div>
                      <div className="col-span-2 mt-1">
                        <p className={`text-center font-bold text-sm ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
                          {getDaysDisplay(approval)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => navigate(`/approvals/${approval.approval_id}`)}
                        className="py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </button>
                      <button
                        onClick={() => navigate(`/approvals/edit/${approval.approval_id}`)}
                        className="py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center justify-center gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>
                      {statusInfo.status !== 'Returned' && statusInfo.status !== 'Cancelled' && (
                        <button
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowReturnModal(true);
                          }}
                          className="col-span-2 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center justify-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Mark as Returned
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{selectedApproval.approval_id}</h3>
              <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-amber-200 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Customer Info */}
              <div className="bg-amber-50 rounded-lg p-3">
                <h4 className="text-sm font-semibold mb-2">Customer</h4>
                <p className="font-medium">{getCustomerName(selectedApproval.customer_id)}</p>
                <p className="text-sm text-gray-600">{getCustomerMobile(selectedApproval.customer_id)}</p>
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Given On</p>
                  <p className="font-semibold">{formatDate(selectedApproval.approval_date || selectedApproval.created_at)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Return By</p>
                  <p className="font-semibold">{formatDate(selectedApproval.return_date)}</p>
                </div>
              </div>
              
              {/* Weight Summary */}
              <div className="bg-purple-50 rounded-lg p-3">
                <h4 className="text-sm font-semibold mb-2">Weight Summary</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-600">Items</p>
                    <p className="text-xl font-bold text-purple-700">{selectedApproval.items?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Gross Wt</p>
                    <p className="text-xl font-bold text-gray-700">{calculateGrossWeight(selectedApproval).toFixed(2)}g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Net Wt</p>
                    <p className="text-xl font-bold text-amber-700">{calculateNetWeight(selectedApproval).toFixed(2)}g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Fine Wt</p>
                    <p className="text-xl font-bold text-indigo-700">{calculateFineWeight(selectedApproval).toFixed(2)}g</p>
                  </div>
                </div>
              </div>
              
              {/* Items List */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Items</h4>
                <div className="space-y-2">
                  {selectedApproval.items?.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">#{idx + 1}</span>
                          <span className="font-semibold">{item.item_type}</span>
                        </div>
                        <span className="text-purple-700 font-medium">{item.purity}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Gross: </span>
                          <span>{item.gross_wt ? `${item.gross_wt}g` : (item.weight_type === 'Fixed' ? `${item.weight_fixed}g` : `${item.weight_min}-${item.weight_max}g`)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Less: </span>
                          <span>{item.less_wt ? `${item.less_wt}g` : '0g'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Net: </span>
                          <span className="font-semibold text-amber-700">{item.net_wt ? `${item.net_wt}g` : (item.gross_wt ? `${item.gross_wt}g` : (item.weight_fixed ? `${item.weight_fixed}g` : 'N/A'))}</span>
                        </div>
                      </div>
                      <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Qty: </span>
                          <span>{item.quantity || 1}</span>
                        </div>
                        {item.size && (
                          <div>
                            <span className="text-gray-500">Size: </span>
                            <span>{item.size}</span>
                          </div>
                        )}
                      </div>
                      {item.tag_number && (
                        <p className="text-xs mt-1"><span className="text-gray-500">Tag#: </span>{item.tag_number}</p>
                      )}
                      {item.public_remark && (
                        <p className="text-xs mt-1 text-gray-600">{item.public_remark}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Signatures */}
              {(selectedApproval.management_signature || selectedApproval.customer_signature) && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Signatures</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedApproval.management_signature && (
                      <div className="border rounded-lg p-2">
                        <p className="text-xs text-gray-500 mb-1">Management</p>
                        <img src={selectedApproval.management_signature} alt="Management" className="h-16 object-contain" />
                        {selectedApproval.management_signature_name && (
                          <p className="text-xs text-center mt-1">{selectedApproval.management_signature_name}</p>
                        )}
                      </div>
                    )}
                    {selectedApproval.customer_signature && (
                      <div className="border rounded-lg p-2">
                        <p className="text-xs text-gray-500 mb-1">Customer</p>
                        <img src={selectedApproval.customer_signature} alt="Customer" className="h-16 object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {showExtendModal && selectedApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4">Extend Return Date</h3>
            <p className="text-sm text-gray-600 mb-4">
              Current return date: {formatDate(selectedApproval.return_date)}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Extend by (days)</label>
                <div className="flex gap-2">
                  {[2, 3, 5, 7, 14].map(days => (
                    <button
                      key={days}
                      onClick={() => setExtendDays(days)}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold ${
                        extendDays === days 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      +{days}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Reason (optional)</label>
                <textarea
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                  rows="2"
                  placeholder="Enter reason for extension"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setSelectedApproval(null);
                }}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleExtendReturn}
                className="flex-1 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600"
              >
                Extend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && selectedApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4">Mark as Returned</h3>
            <p className="text-sm text-gray-600 mb-4">
              Confirm that all items from approval <span className="font-semibold">{selectedApproval.approval_id}</span> have been returned.
            </p>
            
            <div className="bg-amber-50 rounded-lg p-3 mb-4">
              <p className="text-sm"><span className="text-gray-600">Items:</span> <span className="font-semibold">{selectedApproval.items?.length || 0}</span></p>
              <p className="text-sm"><span className="text-gray-600">Gross Weight:</span> <span className="font-semibold">{calculateGrossWeight(selectedApproval).toFixed(2)}g</span></p>
              <p className="text-sm"><span className="text-gray-600">Net Weight:</span> <span className="font-semibold text-amber-700">{calculateNetWeight(selectedApproval).toFixed(2)}g</span></p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Notes (optional)</label>
              <textarea
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                rows="2"
                placeholder="Any notes about the return"
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowReturnModal(false);
                  setSelectedApproval(null);
                }}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkReturned}
                className="flex-1 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600"
              >
                Confirm Return
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalReports;
